<script setup lang="ts">
import { useFetch } from "@/composables/useFetch.js";
import {uri} from "@/constants/config.js"
import {Dialog} from "@/base-components/Headless";
import Button from "@/base-components/Button";
import Lucide from "@/base-components/Lucide";
import {ref, watch} from "vue";
import SelectDiv from "@/components/SelectDiv.vue";
import {currencyReadable, formatSize} from "@/composables/utility";
import Alert from "@/base-components/Alert";

const {isOpen, iface} = defineProps({
  isOpen: {type: Boolean, required: true},
  iface: {type: Object, required: true},
});
const emits = defineEmits(['close', 'accept']);

const isLoading = ref(true)
const pricingList = ref<{key: string, price: string}[]>([]);
const selectedPrice = ref<{key: string, price: string}>();
async function loadAssets() {
  isLoading.value = true
  const {data, error} = await useFetch(uri.api.iaas.interface.availableTraffics(iface.id))
  isLoading.value = false
  if (error) return;
  pricingList.value = data
}

function accept() {
  if (!selectedPrice.value) return;
  emits('accept', iface, selectedPrice.value);
  emits('close')
}

loadAssets()

</script>

<template>
  <Dialog
      size="xl"
      :open="isOpen"
      @close="emits('close')"
  >
    <Dialog.Panel
        class="rounded-md"
    >
      <Dialog.Title class="bg-primary rounded-t-md text-white">
        <div class="relative w-full h-5">
          <strong class="text-base">
            <Lucide icon="TrafficCone" class="inline-block"/>
            {{ $t('title.reserveTraffic') }}
          </strong>
        </div>
        <div @click="emits('close')" class="absolute top-3 end-3 mt-0 me-1 cursor-pointer w-5 h-5 bg-secondary rounded-full">
          <Lucide icon="X" class="w-5 h-5 font-bold text-black"/>
        </div>
      </Dialog.Title>
      <Alert
          variant="outline-info"
          class="flex items-center mb-2"
          v-slot="{ dismiss }"
      >
        <Lucide icon="AlertOctagon" class="w-6 h-6 mr-2"/>
<span class="p-1">        ترافیک رزرو پس از پایان بسته ترافیک فعلی بصورت خودکار فعال می‌شود و تا پایان دوره اعتبار خواهد داشت.</span>
        <Alert.DismissButton
            type="button"
            class="btn-close left-0 right-auto"
            @click="dismiss"
            aria-label="Close"
        >
          <Lucide icon="X" class="w-4 h-4"/>
        </Alert.DismissButton>
      </Alert>
      <free-style-shimmer v-if="isLoading" height="100px" width="100$" class="p-0 m-5"/>
      <div class="flex mt-2 mr-2">
        <SelectDiv v-for="option in pricingList" :key="option.key" :is-selected="selectedPrice === option" @click="selectedPrice = option" class="ml-1">
          <div class="grid grid-cols-1 gap-2">
            <div class="flex items-center">
              <h2 class="mr-5 font-bold truncate">
                {{ formatSize(option.key, 'mb', 0) }}
              </h2>
            </div>
            <div class="flex items-center">
              <h2 class="mr-5 text-xs truncate" dir="rtl">
                {{
                  currencyReadable(option.price)
                }}
                ریال
              </h2>
            </div>
          </div>
        </SelectDiv>
      </div>
      <Dialog.Footer>
        <div class="flex col-md-12 items-end justify-end gap-2">
          <Button variant="outline-secondary" @click="emits('close')" size="sm" class=" shadow-md justify-end items-end">
            {{ $t('button.cancel') }}
          </Button>
          <Button variant="primary" size="sm" @click="accept" class="shadow-md justify-end items-end">
            {{ $t('button.accept') }}
          </Button>
        </div>
      </Dialog.Footer>
    </Dialog.Panel>
  </Dialog>
</template>

<style scoped>

</style>